import { Range } from 'rc-slider';
import { useEffect, useState } from 'react';

import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';

export enum SliderUnit {
  'SECOND',
  'MINUTE',
  'HOUR',
  'DATE',
  'PRICE',
  'THOUSAND',
  'TENTHOUSAND',
}

const unitDescMap = new Map([
  [SliderUnit.SECOND, '3600/시간'],
  [SliderUnit.MINUTE, '60/시간'],
  [SliderUnit.HOUR, '1/시간'],
  [SliderUnit.DATE, '1/일'],
  [SliderUnit.PRICE, '1/원'],
  [SliderUnit.THOUSAND, '1000/천'],
  [SliderUnit.TENTHOUSAND, '10000/만'],
]);

const style = {
  mark_min: {
    top: '3px',
    marginTop: '4px',
    fontSize: '13px',
    color: '#8f8f8f',
    wordBreak: 'keep-all',
    transform: 'none',
  },
  mark_max: {
    top: '3px',
    left: 'auto',
    right: '0',
    marginTop: '4px',
    fontSize: '13px',
    color: '#8f8f8f',
    wordBreak: 'keep-all',
    transform: 'none',
  },
  label_name: {
    marginRight: '4px',
    color: '#666666',
  },
  label_value: {
    color: '#5398f5',
  },
  slider: {
    margin: '20px 0 19px',
    height: '21px',
    padding: '10px 0 9px',
  },
  track: {
    height: '2px',
    backgroundColor: '#5398f5',
  },
  rail: {
    height: '2px',
    backgroundColor: '#d6dde2',
  },
  handle: {
    marginTop: '-10px',
    width: '21px',
    height: '21px',
    border: 'none',
    borderRadius: '15px',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.15)',
    backgroundColor: '#ffffff',
  },
  dot: {
    display: 'none',
  },
};

export interface IRange {
  min?: number;
  max?: number;
}

interface IProps {
  min: number;
  max: number;
  selectedRange?: IRange;
  setValue: Function;
  unit: SliderUnit;
}

function hasRange(prices: number[]) {
  return prices[0] !== prices[1];
}

function Slider({
  min,
  max,
  unit: inputUnit,
  setValue,
  selectedRange: propsSelectedRange,
}: IProps) {
  const selectedUnit = unitDescMap.get(inputUnit) || '1';
  const [unit, unitDesc] = selectedUnit.split('/');

  const minValue = Math.floor(min / +unit) * +unit;
  const maxValue = Math.ceil(max / +unit) * +unit;

  const selectedRange = {
    min: propsSelectedRange?.min || minValue,
    max: propsSelectedRange?.max || maxValue,
  };

  const currentMinValue = Math.floor(selectedRange.min / +unit) * +unit;
  const currentMaxValue = Math.ceil(selectedRange.max / +unit) * +unit;

  const [range, setRange] = useState([currentMinValue, currentMaxValue]);

  useEffect(
    () => setRange([currentMinValue, currentMaxValue]),
    [currentMinValue, currentMaxValue],
  );

  const minDesc = `${withPriceSeperator(minValue / +unit)}${unitDesc}`;
  const maxDesc = `${withPriceSeperator(maxValue / +unit)}${unitDesc}`;

  const marks: { [key: number]: { style: any; label: string } } = {};
  marks[minValue] = {
    style: style.mark_min,
    label: minDesc,
  };
  marks[maxValue] = {
    style: style.mark_max,
    label: maxDesc,
  };

  return (
    <Range
      min={minValue}
      max={maxValue}
      value={range}
      step={+unit}
      allowCross={false}
      onChange={val => hasRange(val) && setRange(val)}
      onAfterChange={val =>
        hasRange(val) &&
        setValue({
          min: val[0] > min ? val[0] : undefined,
          max: val[1] < max ? val[1] : undefined,
        })
      }
      marks={marks}
      trackStyle={[style.track]}
      railStyle={style.rail}
      handleStyle={[style.handle, style.handle]}
      dotStyle={style.dot}
      style={style.slider}
    />
  );
}

export default Slider;
